// Browser copy of server/drawingFormat.js. Keep the two behaviourally
// identical; server/drawingFormat.parity.test.js checks both against the
// same fixtures and compares their rendered output.
//
// A drawing is { version, width, height, strokes: [{ tool, size, points }] }.
// Coordinates are integers on a fixed logical canvas so every device, screen
// density and orientation describes the same picture. `points` is a flat
// [x0, y0, x1, y1, ...] array; a single point is a dot.

const DRAWING_FORMAT_VERSION = 1;
const DRAWING_WIDTH = 1600;
const DRAWING_HEIGHT = 1000;
const DRAWING_TOOLS = Object.freeze(["pen", "eraser"]);
const PEN_WIDTHS = Object.freeze([3, 6, 12]);
const ERASER_WIDTHS = Object.freeze([16, 32, 64]);
const INK_COLOUR = "#111827";
const DRAWING_LIMITS = Object.freeze({
  maxStrokes: 1500,
  maxPointsPerStroke: 4000,
  maxTotalPoints: 60000,
});
// Minimum distance between kept points while drawing, and the tolerance used
// to simplify a finished stroke. Both are well under the thinnest pen width,
// so sampling never changes what the diagram shows.
const MIN_POINT_DISTANCE = 1.5;
const SIMPLIFY_TOLERANCE = 0.6;

function drawingFormatError(message) {
  const error = new Error(message);
  error.status = 400;
  error.code = "invalid-drawing";
  return error;
}

function exactKeys(value, keys) {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  const actual = Object.keys(value);
  return actual.length === keys.length && keys.every((key) => Object.prototype.hasOwnProperty.call(value, key));
}

function strokeWidth(stroke) {
  return (stroke.tool === "eraser" ? ERASER_WIDTHS : PEN_WIDTHS)[stroke.size];
}

// Validates the exact shape and every limit, and returns a canonical copy
// (fixed key order, fresh arrays) so hashing is stable.
function normalizeDrawing(value) {
  if (!exactKeys(value, ["version", "width", "height", "strokes"])) {
    throw drawingFormatError("A drawing must contain only version, width, height and strokes.");
  }
  if (value.version !== DRAWING_FORMAT_VERSION || value.width !== DRAWING_WIDTH || value.height !== DRAWING_HEIGHT) {
    throw drawingFormatError("The drawing format or canvas size is not supported.");
  }
  if (!Array.isArray(value.strokes) || value.strokes.length > DRAWING_LIMITS.maxStrokes) {
    throw drawingFormatError(`A drawing can contain at most ${DRAWING_LIMITS.maxStrokes} strokes.`);
  }
  let totalPoints = 0;
  const strokes = value.strokes.map((stroke) => {
    if (!exactKeys(stroke, ["tool", "size", "points"]) || !DRAWING_TOOLS.includes(stroke.tool) ||
        !Number.isInteger(stroke.size) || stroke.size < 0 || stroke.size > 2 || !Array.isArray(stroke.points)) {
      throw drawingFormatError("A drawing stroke is invalid.");
    }
    const { points } = stroke;
    const count = points.length / 2;
    if (points.length < 2 || points.length % 2 !== 0 || count > DRAWING_LIMITS.maxPointsPerStroke) {
      throw drawingFormatError("A drawing stroke has an invalid number of points.");
    }
    totalPoints += count;
    if (totalPoints > DRAWING_LIMITS.maxTotalPoints) {
      throw drawingFormatError(`A drawing can contain at most ${DRAWING_LIMITS.maxTotalPoints} points.`);
    }
    for (let index = 0; index < points.length; index += 2) {
      const x = points[index];
      const y = points[index + 1];
      if (!Number.isInteger(x) || !Number.isInteger(y) || x < 0 || y < 0 || x > DRAWING_WIDTH || y > DRAWING_HEIGHT) {
        throw drawingFormatError("A drawing point is outside the canvas.");
      }
    }
    return { tool: stroke.tool, size: stroke.size, points: points.slice() };
  });
  return { version: DRAWING_FORMAT_VERSION, width: DRAWING_WIDTH, height: DRAWING_HEIGHT, strokes };
}

function emptyDrawing() {
  return { version: DRAWING_FORMAT_VERSION, width: DRAWING_WIDTH, height: DRAWING_HEIGHT, strokes: [] };
}

function canonicalDrawingJson(drawing) {
  return JSON.stringify(normalizeDrawing(drawing));
}

function drawingStats(drawing) {
  let points = 0;
  let penStrokes = 0;
  drawing.strokes.forEach((stroke) => {
    points += stroke.points.length / 2;
    if (stroke.tool === "pen") penStrokes += 1;
  });
  return { strokes: drawing.strokes.length, points, penStrokes };
}

// Cheap test: no pen stroke means nothing can be visible. A drawing whose ink
// has all been erased is detected from the rendered pixels instead.
function hasPenStroke(drawing) {
  return drawing.strokes.some((stroke) => stroke.tool === "pen");
}

function clampPoint(x, y) {
  return [
    Math.min(DRAWING_WIDTH, Math.max(0, Math.round(x))),
    Math.min(DRAWING_HEIGHT, Math.max(0, Math.round(y))),
  ];
}

// Appends a logical point to a flat points array unless it is closer than
// MIN_POINT_DISTANCE to the last kept point. Returns true when appended.
function appendPoint(points, x, y) {
  const [px, py] = clampPoint(x, y);
  const last = points.length - 2;
  if (last >= 0 && Math.hypot(px - points[last], py - points[last + 1]) < MIN_POINT_DISTANCE) return false;
  points.push(px, py);
  return true;
}

function perpendicularDistance(px, py, ax, ay, bx, by) {
  const dx = bx - ax;
  const dy = by - ay;
  const length = Math.hypot(dx, dy);
  if (length === 0) return Math.hypot(px - ax, py - ay);
  return Math.abs(dy * px - dx * py + bx * ay - by * ax) / length;
}

// Ramer-Douglas-Peucker on a flat points array, iterative so long strokes
// cannot exhaust the stack. End points are always kept.
function simplifyPoints(points, tolerance = SIMPLIFY_TOLERANCE) {
  const count = points.length / 2;
  if (count <= 2) return points.slice();
  const keep = new Uint8Array(count);
  keep[0] = 1;
  keep[count - 1] = 1;
  const stack = [[0, count - 1]];
  while (stack.length > 0) {
    const [start, end] = stack.pop();
    let furthest = -1;
    let furthestDistance = tolerance;
    for (let index = start + 1; index < end; index += 1) {
      const distance = perpendicularDistance(
        points[index * 2], points[index * 2 + 1],
        points[start * 2], points[start * 2 + 1],
        points[end * 2], points[end * 2 + 1]
      );
      if (distance > furthestDistance) {
        furthest = index;
        furthestDistance = distance;
      }
    }
    if (furthest !== -1) {
      keep[furthest] = 1;
      stack.push([start, furthest], [furthest, end]);
    }
  }
  const result = [];
  for (let index = 0; index < count; index += 1) {
    if (keep[index]) result.push(points[index * 2], points[index * 2 + 1]);
  }
  return result;
}

// Draws the ink layer onto a 2D context whose coordinate space is the logical
// canvas multiplied by `scale`. Pen strokes paint ink; eraser strokes remove
// ink (destination-out), so the caller composites the layer over white. The
// browser and Node renderers issue exactly these commands.
function renderDrawing(context, drawing, scale = 1) {
  context.save();
  context.lineCap = "round";
  context.lineJoin = "round";
  context.strokeStyle = INK_COLOUR;
  context.fillStyle = INK_COLOUR;
  drawing.strokes.forEach((stroke) => {
    const width = strokeWidth(stroke) * scale;
    const { points } = stroke;
    context.globalCompositeOperation = stroke.tool === "eraser" ? "destination-out" : "source-over";
    context.beginPath();
    if (points.length === 2) {
      context.arc(points[0] * scale, points[1] * scale, width / 2, 0, Math.PI * 2);
      context.fill();
      return;
    }
    context.lineWidth = width;
    context.moveTo(points[0] * scale, points[1] * scale);
    for (let index = 2; index < points.length; index += 2) {
      context.lineTo(points[index] * scale, points[index + 1] * scale);
    }
    context.stroke();
  });
  context.restore();
}

export {
  DRAWING_FORMAT_VERSION,
  DRAWING_HEIGHT,
  DRAWING_LIMITS,
  DRAWING_TOOLS,
  DRAWING_WIDTH,
  ERASER_WIDTHS,
  INK_COLOUR,
  MIN_POINT_DISTANCE,
  PEN_WIDTHS,
  SIMPLIFY_TOLERANCE,
  appendPoint,
  canonicalDrawingJson,
  drawingStats,
  emptyDrawing,
  hasPenStroke,
  normalizeDrawing,
  renderDrawing,
  simplifyPoints,
  strokeWidth,
};
